import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { X } from 'lucide-react';
import { apiInvoices, apiCustomers } from '../../lib/api';
import { normalizeItems, fmt, fmtDateUS, nextNumber, parseDate, invoicePaid } from '../../lib/invoiceItems';
import { toast } from 'sonner';
import { todayISO } from '../../lib/dates';

import { today as localToday, ymd as localYmd, parseDay } from '../../lib/dates';
export default function InvoiceFormDialog({
  open, setOpen, editing, customers = [], fixedCustomer, fromWorkorder,
  allInvoices = [], onSaved,
}) {
  const [customerId, setCustomerId] = useState('');
  const [date, setDate] = useState(todayISO());
  const [due, setDue] = useState('');
  const [status, setStatus] = useState('Open');
  const [items, setItems] = useState([{ desc: '', qty: 1, rate: 0 }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const d30 = new Date(); d30.setDate(d30.getDate() + 30);
    if (editing) {
      setCustomerId(editing.customerId || fixedCustomer?.id || '');
      const dt = parseDate(editing.date);
      setDate(isNaN(dt) ? localToday() : localYmd(dt));
      const due0 = parseDate(editing.due);
      setDue(isNaN(due0) ? localYmd(d30) : localYmd(due0));
      setStatus(editing.status || 'Open');
      setItems(normalizeItems(editing.items, editing.total));
    } else if (fromWorkorder) {
      setCustomerId(fixedCustomer?.id || fromWorkorder.customerId || '');
      setDate(localToday());
      setDue(localYmd(d30));
      setStatus('Open');
      setItems([{ desc: fromWorkorder.service || fromWorkorder.name || 'Service', qty: 1, rate: Number(fromWorkorder.invoiceTotal) || 0 }]);
    } else {
      setCustomerId(fixedCustomer?.id || customers[0]?.id || '');
      setDate(localToday());
      setDue(localYmd(d30));
      setStatus('Open');
      setItems([{ desc: '', qty: 1, rate: 0 }]);
    }
  }, [open, editing, fromWorkorder, fixedCustomer]);

  const total = items.reduce((a, i) => a + (Number(i.qty) || 0) * (Number(i.rate) || 0), 0);
  const updateItem = (i, k, v) => { const a = [...items]; a[i] = { ...a[i], [k]: v }; setItems(a); };
  const addItem = () => setItems([...items, { desc: '', qty: 1, rate: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const submit = async () => {
    const cust = fixedCustomer || customers.find((c) => c.id === customerId);
    if (!cust) { toast.error('Choose a customer'); return; }
    const cleaned = items.filter((i) => i.desc && Number(i.qty));
    if (!cleaned.length) { toast.error('Add at least one line item'); return; }
    setSaving(true);
    const paid = status === 'Paid' ? total : (editing ? invoicePaid(editing) : 0);
    try {
      if (editing) {
        await apiInvoices.update(editing.id, {
          ...editing, customerId: cust.id, customerName: cust.name,
          customerAccountNumber: cust.accountNumber || '', date: fmtDateUS(parseDay(date)),
          due: fmtDateUS(parseDay(due)), status, items: cleaned, total, subtotal: total,
          paid, amountDue: Math.max(0, total - paid),
        });
        toast.success('Invoice updated');
      } else {
        const num = nextNumber(allInvoices, 6000);
        await apiInvoices.create({
          id: `INV-${num}`, number: String(num), customerId: cust.id, customerName: cust.name,
          customerAccountNumber: cust.accountNumber || '', customerEmail: cust.email || '',
          date: fmtDateUS(parseDay(date)), due: fmtDateUS(parseDay(due)), status,
          items: cleaned, total, subtotal: total, paid, amountDue: Math.max(0, total - paid),
          createdDate: fmtDateUS(new Date()),
        });
        // bump customer balance for open invoices
        if (status !== 'Paid') {
          await apiCustomers.update(cust.id, { ...cust, balance: (Number(cust.balance) || 0) + total }).catch(() => {});
        }
        toast.success('Invoice created');
      }
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error('Could not save invoice');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl" data-testid="invoice-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.id}` : 'New Invoice'}</DialogTitle>
          <DialogDescription>{fixedCustomer ? `For ${fixedCustomer.name}` : 'Create or update a customer invoice.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Customer</Label>
              {fixedCustomer ? (
                <Input value={fixedCustomer.name} disabled className="mt-1.5" />
              ) : (
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger className="mt-1.5" data-testid="invoice-customer-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="Overdue">Overdue</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                  <SelectItem value="Draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Invoice Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" /></div>
            <div><Label>Due Date</Label><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="mt-1.5" /></div>
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-[1fr_80px_100px_100px_40px] gap-2 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
              <div>Description</div><div>Qty</div><div>Rate</div><div className="text-right">Amount</div><div></div>
            </div>
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_80px_100px_100px_40px] gap-2 px-3 py-2 items-center border-t border-slate-100">
                <Input value={it.desc} onChange={(e) => updateItem(i, 'desc', e.target.value)} placeholder="Service description" className="h-9" data-testid={`invoice-item-desc-${i}`} />
                <Input type="number" value={it.qty} onChange={(e) => updateItem(i, 'qty', e.target.value)} className="h-9" />
                <Input type="number" value={it.rate} onChange={(e) => updateItem(i, 'rate', e.target.value)} className="h-9" />
                <div className="text-right font-semibold tabular-nums text-slate-900">{fmt((Number(it.qty) || 0) * (Number(it.rate) || 0))}</div>
                <button onClick={() => removeItem(i)} className="text-slate-400 hover:text-red-600"><X className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={addItem} className="w-full text-sm text-[#6FA82C] hover:bg-emerald-50 py-2 border-t border-slate-100 font-semibold">+ Add Line</button>
          </div>
          <div className="flex justify-end items-center gap-4 pt-2">
            <span className="text-slate-500">Total</span>
            <span className="text-2xl font-extrabold text-slate-900 tabular-nums">{fmt(total)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="invoice-save-btn" onClick={submit} disabled={saving} className="bg-[#8BC53F] hover:bg-[#6FA82C]">
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
